import { useMemo, useRef, useState } from 'react';

import { type QueryClient, keepPreviousData, skipToken, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import type { LensDocumentData, LensState } from '~/pages/LeLens/document/types';

import { reportingLeLensDataPath } from '~/consts/reportingLeLensApi';

import { loadLensDocumentStream } from '~/utils/loadLensDocumentStream';
import { requestWithTimeout } from '~/utils/requestWithTimeout';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { reportingLeLensQueryKeys } from './useGetReportingLeLens';

const DATA_TIMEOUT_MS = 90_000;

type Held = { period?: string; data: LensDocumentData };

export function canReuseFilterOptions(prior: { period?: string; data: { options?: LensDocumentData['options'] } } | undefined, state: LensState): boolean {
  return Boolean(prior && prior.period === state.period && Object.keys(state.filters ?? {}).every((column) => prior.data.options?.[column] !== undefined));
}

export function runRefreshOnce<T>(pending: { current: boolean }, active: boolean, onStart: () => void, refetch: () => Promise<T>): Promise<T | undefined> {
  if (pending.current || active) return Promise.resolve(undefined);
  pending.current = true;
  try {
    onStart();
    return refetch().finally(() => { pending.current = false; });
  } catch (error) {
    pending.current = false;
    return Promise.reject(error);
  }
}

const lensHeldKey = (lensId: string, lensVersion: number | undefined) => ['lens-held', lensId, lensVersion] as const;

export function carryLensData(client: QueryClient, lensId: string, from: number, to: number) {
  const prefix = [...reportingLeLensQueryKeys.detail(lensId), 'data', 'document'];
  const versionAt = prefix.length + 1;
  for (const query of client.getQueryCache().findAll({ queryKey: prefix })) {
    if (query.queryKey[versionAt] !== from || query.state.status !== 'success') continue;
    const target = [...query.queryKey.slice(0, versionAt), to, ...query.queryKey.slice(versionAt + 1)];
    client.setQueryData(target, { ...(query.state.data as LensDocumentData), lensVersion: to });
  }
  const held = client.getQueryData<Held>(lensHeldKey(lensId, from));
  if (held) client.setQueryData(lensHeldKey(lensId, to), held);
}

export function withLentBlock(data: LensDocumentData, from: string, to: string): LensDocumentData {
  const source = data.blocks[from];
  if (!source) return data;
  const target = data.blocks[to];
  const planned = (block: typeof source | undefined) => block?.queries ?? (block?.query ? [block.query] : []);
  return { ...data, blocks: { ...data.blocks, [to]: { ...(target ?? source), queries: [...new Set([...planned(target), ...planned(source)])] } } };
}

export function lendBlockData(client: QueryClient, lensId: string, version: number, from: string, to: string) {
  const prefix = [...reportingLeLensQueryKeys.detail(lensId), 'data', 'document'];
  const versionAt = prefix.length + 1;
  const lend = (data: LensDocumentData) => withLentBlock(data, from, to);
  for (const query of client.getQueryCache().findAll({ queryKey: prefix })) {
    if (query.queryKey[versionAt] === version && query.state.status === 'success') client.setQueryData(query.queryKey, lend(query.state.data as LensDocumentData));
  }
  const held = client.getQueryData<Held>(lensHeldKey(lensId, version));
  if (held) client.setQueryData<Held>(lensHeldKey(lensId, version), { ...held, data: lend(held.data) });
}

function withoutParams(state: LensState, names: Array<string>): LensState {
  if (!names.length || !state.params) return state;
  const { params, ...rest } = state;
  const kept = Object.entries(params).filter(([name]) => !names.includes(name));
  return kept.length ? { ...rest, params: Object.fromEntries(kept) } : rest;
}

function chosenLocally(data: LensDocumentData | undefined, state: LensState): LensDocumentData | undefined {
  if (!data || !Object.values(data.blocks).some((block) => block.fold)) return data;
  const params = { ...data.params, ...Object.fromEntries((data.folded ?? []).flatMap((name) => (state.params?.[name] !== undefined ? [[name, state.params[name]]] : []))) };
  const queries = { ...data.queries };
  for (const block of Object.values(data.blocks)) {
    if (!block.fold) continue;
    const chosen = String(params[block.fold.param] ?? '');
    for (const id of block.queries ?? (block.query ? [block.query] : [])) {
      const envelope = queries[id];
      if (envelope) queries[id] = { ...envelope, columns: envelope.columns.filter((column) => column.role !== 'measure' || !block.fold!.measures.includes(column.name) || column.name === chosen) };
    }
  }
  return { ...data, params, queries };
}

function carried(next: LensDocumentData, prior: LensDocumentData | undefined, reuseOptions: boolean): LensDocumentData {
  if (!prior) return next;
  const wanted = [...Object.values(next.blocks).flatMap((block) => block.queries ?? (block.query ? [block.query] : [])), ...(next.lead ?? [])];
  const held = Object.fromEntries(wanted.flatMap((id) => (prior.queries[id]?.key && prior.queries[id].key === next.keys?.[id] ? [[id, prior.queries[id]]] : [])));
  return { ...next, queries: { ...held, ...next.queries }, options: reuseOptions ? prior.options : next.options };
}

export function carryHeldData(client: QueryClient, lensId: string, from: number, to: number) {
  const held = client.getQueryData<Held>(lensHeldKey(lensId, from));
  if (held) client.setQueryData<Held>(lensHeldKey(lensId, to), { ...held, data: { ...held.data, folded: undefined } });
}

export function useGetReportingLensDocumentData(lensId: string | undefined, lensVersion: number | undefined, boardState: LensState, shownTabs?: Array<string>) {
  const client = useQueryClient();
  const generation = useRef(0);
  const fresh = useRef(false);
  const refreshPending = useRef(false);
  const heldKey = lensHeldKey(lensId ?? '', lensVersion);
  const { data: held } = useQuery<Held>({ queryKey: heldKey, queryFn: skipToken, staleTime: Infinity });
  const state = withoutParams(boardState, held?.data.folded ?? []);
  const identity = JSON.stringify([lensId, lensVersion, state]);
  const shown = useRef(shownTabs);
  shown.current = shownTabs;
  const [partial, setPartial] = useState<{ identity: string; data: LensDocumentData }>();
  const query = useQuery({
    queryKey: [...reportingLeLensQueryKeys.detail(lensId ?? ''), 'data', 'document', state, lensVersion],
    queryFn: async ({ signal }): Promise<LensDocumentData & { loadedTabs?: Array<string> }> => {
      const own = ++generation.current;
      const tabs = shown.current;
      const refresh = fresh.current;
      fresh.current = false;
      const prior = refresh ? undefined : client.getQueryData<Held>(heldKey);
      const reuseOptions = canReuseFilterOptions(prior, state);
      const known = prior ? Object.values(prior.data.queries).flatMap((envelope) => (envelope.status !== 'error' && envelope.key ? [envelope.key] : [])) : [];
      setPartial(undefined);
      const streamed = await loadLensDocumentStream(
        resolveReportingLeLensUrl(reportingLeLensDataPath(lensId ?? '')),
        { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ state, stream: true, ...(tabs ? { tabs } : {}), ...(known.length ? { known } : {}), ...(reuseOptions ? { reuseOptions } : {}), ...(refresh ? { refresh } : {}) }), signal },
        (data) => {
          if (!signal.aborted && own === generation.current) setPartial({ identity, data: carried(data, prior?.data, reuseOptions) });
        },
        DATA_TIMEOUT_MS,
        'This Lens’s numbers are taking longer than expected. Nothing was changed, so try again.',
      );
      const data = carried(streamed, prior?.data, reuseOptions);
      client.setQueryData<Held>(heldKey, { period: state.period, data });
      return tabs ? { ...data, loadedTabs: tabs } : data;
    },
    enabled: Boolean(lensId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: keepPreviousData,
  });
  const loaded = query.data?.loadedTabs;
  const missing = shownTabs && loaded && !query.isPlaceholderData ? shownTabs.filter((tab) => !loaded.includes(tab)) : [];
  const extra = useQueries({
    queries: missing.map((tab) => ({
      queryKey: [...reportingLeLensQueryKeys.detail(lensId ?? ''), 'data', 'document', state, lensVersion, 'tab', tab],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        requestWithTimeout<LensDocumentData>(
          resolveReportingLeLensUrl(reportingLeLensDataPath(lensId ?? '')),
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ state, tabs: [tab], tabsOnly: true }), signal },
          'This tab’s numbers are taking longer than expected. Nothing was changed, so try again.',
          DATA_TIMEOUT_MS,
        ),
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: false,
    })),
  });
  const streamed = partial?.identity === identity && (query.isFetching || query.isError) ? partial.data : undefined;
  const base = streamed ?? query.data;
  const merged = base && extra.some((entry) => entry.data)
    ? extra.reduce<LensDocumentData>((data, entry) => (entry.data ? { ...data, blocks: { ...data.blocks, ...entry.data.blocks }, queries: { ...data.queries, ...entry.data.queries }, problems: [...data.problems, ...entry.data.problems] } : data), base)
    : base;
  const localParams = JSON.stringify(boardState.params ?? {});
  const data = useMemo(() => chosenLocally(merged, boardState), [merged, localParams]);
  const settling = query.isFetching && query.data !== undefined && query.data.lensVersion !== lensVersion;
  const refresh = () => runRefreshOnce(refreshPending, query.isFetching, () => { fresh.current = true; }, () => query.refetch());
  return { ...query, data, settling, refresh, isPlaceholderData: streamed || held ? false : query.isPlaceholderData };
}
